import {
    useMutation,
    useQueries,
    useQuery,
    useQueryClient,
} from "@tanstack/react-query";
import axios from "axios";
import config from "../../config";

const fetchList = (doctype, filters, fields, sortBy, parent, limit) => {
    const params = {
        fields: JSON.stringify(fields ? fields : ["*"]),
        limit_page_length: limit ? limit : 0,
    };
    if (filters) {
        params["filters"] = JSON.stringify(filters);
    }

    if (parent) {
        params["parent"] = parent;
    }
    if (sortBy) {
        params["order_by"] = sortBy;
    }

    return axios.get(`resource/` + doctype, { params });
};

export const useFrappeToken = (enabled) => {
    const key = [`frappe_token`];
    const data = useQuery(
        key,
        async () => {
            const response = await axios.get(`method/custom_api.api.getToken`);
            return response.data.message;
        },
        {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            cacheTime: Infinity,
            enabled: enabled,
        }
    );

    return data;
};

export const useGetDocTypeList = ({
    qKey,
    doctype,
    filters,
    fields,
    sortBy,
    limit,
}) => {
    let key = [];
    if (qKey) {
        key = qKey;
    } else {
        key = [`GetDocList_${doctype}${limit ? "_:" + limit + ":" : ""}`];
        if (filters) {
            key.push(...filters);
        }
    }

    const data = useQuery(
        key,
        async () => {
            const response = await fetchList(
                doctype,
                filters,
                fields,
                sortBy,
                null,
                limit
            );
            return response.data.data;
        },
        {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: true,
            cacheTime: 1000 * 60 * 10,
            enabled: !!doctype,
        }
    );

    return data;
};

export const useGetDocLinkList = ({
    doctype,
    filters,
    fields,
    sortBy,
    parent,
    parentItem,
}) => {
    const key = [`GetDocLinkList_${doctype}_${parent}_${parentItem}`];
    const data = useQuery(
        key,
        async () => {
            const response = await fetchList(doctype, filters, fields, sortBy);
            return response.data.data;
        },
        {
            refetchOnWindowFocus: false,
            refetchOnMount: false,

            cacheTime: Infinity,
            enabled: !!doctype && !!parent && !!parentItem,
        }
    );

    return data;
};

export const useGetMultiDoctypeList = (doctypes) => {
    const defaultFilters = [["docstatus", "!=", 0]]; //filter deleted docs
    const sortBy = "modified desc";
    const dataQueries = useQueries({
        queries: doctypes.map((doctype) => {
            return {
                queryKey: [
                    `GetChildList_${doctype.parent}_${doctype.doctype}_${doctype.fieldname}`,
                ],
                queryFn: async () =>
                    fetchList(
                        doctype.doctype,
                        defaultFilters,
                        null,
                        sortBy,
                        doctype.parent
                    ),
                enabled: !!doctype && !!doctype.doctype && !!doctype.parent,
            };
        }),
    });
    return dataQueries;
};

export const useGetChildDoctypeList = ({
    doctype,
    filters,
    fields,
    sortBy,
    parent,
}) => {
    const { data } = useQuery(
        [`GetDocList_${parent}_${doctype}`],
        async () => {
            try {
                const response = await fetchList(
                    doctype,
                    filters,
                    fields,
                    sortBy,
                    parent
                );
                return response.data.data;
            } catch (e) {
                throw e;
            }
        },
        {
            enabled: !!doctype && !!parent,
        }
    );
    return data;
};

const fetchMetadata = (doctype) => {
    const params = {
        doctype: doctype,
    };
    return axios.get(`method/custom_api.api.get_metadata`, { params });
};

export const useGetDocMetaMutation = () => {
    return useMutation(async ({ doctype }) => {
        const data = await fetchMetadata(doctype);
        return data;
    });
};

export const useGetMultiDocMeta = (doctypes) => {
    const dataQueries = useQueries({
        queries: doctypes.map(
            (doctype) => {
                return {
                    queryKey: [`GetChildMetadata_${doctype.fieldname}`],
                    queryFn: async () => fetchMetadata(doctype.doctype),
                    enabled: !!doctype,
                    cacheTime: Infinity,
                };
            },
            {
                enabled: !!doctypes,
            }
        ),
    });
    return dataQueries;
};

export const useGetMultipleDoctypesMeta = (doctypes = []) => {
    const data = useQuery(
        ["useGetMultipleDoctypesMeta", doctypes ? doctypes.toString() : ""],
        async () => {
            const params = {
                doctypes: JSON.stringify(doctypes),
            };
            const response = await axios.get(
                `method/custom_api.api.get_multiple_metadata`,
                { params }
            );

            const _meta = response.data.message;
            _meta.data = _meta.fields;
            return _meta;
        },
        {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            enabled: !!doctypes && doctypes.length > 0,
            cacheTime: Infinity,
        }
    );

    return data;
};

export const useSiteSettings = () => {
    const data = useQuery(
        [`SiteSettings`],
        async () => {
            const params = {
                doctype: "Site Settings",
                with_parent: 1,
                name: "Site Settings",
            };
            const response = await axios.get(
                "method/frappe.desk.form.load.getdoc",
                { params }
            );
            return response.data;
        },
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            enabled: !!localStorage.getItem("logged"),
            cacheTime: 5 * 60 * 1000,
        }
    );
    return data;
};

export const useSiteSettingsMutation = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ payload, action = "Save" }) => {
            const data = await axios.post(
                `method/frappe.desk.form.save.savedocs`,
                {
                    action: action,
                    doc: JSON.stringify(payload),
                }
            );
            return data;
        },
        {
            onSuccess: () => {
                queryClient.refetchQueries(["SiteSettings"]);
            },
            onError: () => {},
        }
    );
};

export const useGetDocMetadata = (doctype) => {
    const data = useQuery(
        ["GetMetadata_" + doctype],
        async () => {
            const response = await fetchMetadata(doctype);

            const _meta = response.data.message;
            _meta.data = _meta.fields;
            return _meta;
        },
        {
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            refetchOnReconnect: false,
            enabled: !!doctype,
            cacheTime: Infinity,
        }
    );

    return data;
};

const fetchDoctypeShort = (doctype, filters, labelField, parent) => {
    const params = {
        // fields: labelField ? JSON.stringify(["name", labelField]) : JSON.stringify(["name"]),
        fields: JSON.stringify(["*"]),
        limit_page_length: 0,
    };

    if (filters) {
        params.filters = JSON.stringify(filters);
    }

    if (parent) {
        params.parent = parent;
    }
    return axios.get(`resource/` + doctype, { params });
};

export const useGetDocTypeListShort = ({
    doctype,
    filters,
    labelField,
    selectLabel,
    parent,
    qKey,
}) => {
    let key = [`GetDocTypeListShort_${doctype}${parent ? `_${parent}` : ""}`];
    if (qKey) {
        key = qKey;
    } else {
        if (filters && filters.length > 0) {
            key.push(filters[0][2]);
        }
    }
    const { data } = useQuery(
        key,
        async () => {
            const response = await fetchDoctypeShort(
                doctype,
                filters,
                labelField,
                parent
            );
            if (!labelField || labelField == "") {
                // return response.data.data;
                return response.data.data.map(({ name }) => name);
            } else {
                return response.data.data;
            }
        },
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            cacheTime: Infinity,
            keepPreviousData: true,
            enabled: !!doctype,
        }
    );
    return data;
};

export const useGetDocTypeListShortMutation = () => {
    return useMutation(
        async ({ doctype, filters, labelField, selectLabel, parent }) => {
            return await fetchDoctypeShort(
                doctype,
                filters,
                labelField,
                parent
            );
        }
    );
};

const loadDoctype = (doctype, name, parent) => {
    const params = {
        doctype: doctype,
        name: name,
    };
    if (parent) {
        params.parent = parent;
    }

    return axios.get(`method/frappe.desk.form.load.getdoc`, { params });
};

export const useLoadDoctypeMitation = () => {
    return useMutation(async ({ doctype, name }) => {
        const data = await loadDoctype(doctype, name);
        return data;
    });
};

export const useLoadDoctype = (doctype, name, parent) => {
    const data = useQuery(
        [`LoadDoctype_${doctype}`, name],
        async () => {
            const response = await loadDoctype(doctype, name, parent);
            return response.data;
        },
        {
            refetchOnMount: false,
            refetchOnWindowFocus: false,
            refetchOnReconnect: false,
            enabled: !!doctype && !!name,
            cacheTime: 5 * 60 * 1000,
        }
    );
    return data;
};

const getDocTypeData = async (doctype, name) => {
    return await axios.get(`resource/${doctype}/${name}`);
};

export const useGetDocTypeDetails = (doctype, name, onSuccess, onError) => {
    const queryClient = useQueryClient();
    return useQuery(
        ["GetDocTypeDetails_" + name],
        async () => {
            const { data } = await getDocTypeData(doctype, name);
            return data.data;
        },
        {
            onSuccess,
            onError,
            enabled: !!doctype && !!name,
        }
    );
};

export const useGetComments = (doctype, itemName) => {
    return useQuery(
        [`GetComments_${doctype}`, itemName],
        async () => {
            const params = {
                doctype: doctype,
                docname: itemName,
            };
            const { data } = await axios.get(
                "method/custom_api.api.getComments",
                { params }
            );
            return data;
        },
        {
            enabled: !!doctype && !!itemName,
        }
    );
};

const saveDoc = (payload, action = "Save") => {
    return axios.post(`method/frappe.desk.form.save.savedocs`, {
        action: action,
        doc: JSON.stringify(payload),
    });
};

export const useSave = () => {
    return useMutation(async ({ doc }) => {
        const data = await axios.post(config.baseUrl + "/frappe.client.save", {
            doc,
        });
        return data;
    });
};

export const useSaveDoc = (doctype, doctypeName, success) => {
    const queryClient = useQueryClient();

    return useMutation(
        async ({ payload, action = "Save" }) => {
            const data = await saveDoc(payload, action);
            return data;
        },
        {
            onSuccess: () => {
                queryClient.refetchQueries(["GetDocList_" + doctype]);
                if (doctypeName) {
                    queryClient.refetchQueries([
                        `LoadDoctype_${doctype}`,
                        doctypeName,
                    ]);
                }
                if (success) {
                    success();
                }
            },
            retry: false,
            onError: () => {},
        }
    );
};

const addComment = (doctype, name, content, user) => {
    const payload = {
        reference_doctype: doctype,
        reference_name: name,
        comment_email: user,
        comment_by: user,
        content: content,
    };

    return axios.post(`method/frappe.desk.form.utils.add_comment`, payload);
};

export const useAddComment = () => {
    return useMutation(async ({ doctype, name, content, user }) => {
        const data = await addComment(doctype, name, content, user);
        return data;
    });
};

const updateDoctype = (doctype, docname, formdata) => {
    return axios.put(`resource/${doctype}/${docname}`, { ...formdata });
};

export const useUpdateDoctype = () => {
    const queryClient = useQueryClient();
    return useMutation(
        async ({ doctype, docname, formdata }) => {
            const data = await updateDoctype(doctype, docname, formdata);
            return data;
        },
        {
            onSuccess: (data) => {},
        }
    );
};

const deleteDoctype = (doctype, name) => {
    return axios.post(`method/frappe.client.delete`, { doctype, name });
};

export const useDeleteDoctype = (onSuccess) => {
    return useMutation(
        async ({ doctype, name }) => {
            const data = await deleteDoctype(doctype, name);
            return data;
        },
        {
            onSuccess: () => {
                onSuccess?.();
            },
            onError: (err) => {
                console.log(err);
                // errorToast(escapeHTML(JSON.parse(JSON.parse(err.response.data._server_messages)[0]).message));
            },
        }
    );
};

const generateUserKeys = (user) => {
    return axios.post(
        `method/frappe.core.doctype.user.user.generate_keys`,
        user
    );
};

export const useGenerateUserKey = () => {
    return useMutation(async (user) => {
        const data = await generateUserKeys(user);
        return data;
    });
};

export const useGetWorkflowActions = () => {
    return useMutation(async ({ doctype, itemName }) => {
        const doc = {
            name: itemName,
            doctype,
        };
        const data = await axios.post(
            "method/frappe.model.workflow.get_transitions",
            { doc }
        );
        return data;
    });
};

export const useApplyWorkflowActions = () => {
    return useMutation(async ({ doc, action }) => {
        const payload = {
            doc: JSON.stringify(doc),
            action,
        };
        const data = await axios.post(
            "method/frappe.model.workflow.apply_workflow",
            payload
        );
        return data;
    });
};

// export const useSaveUserSettings = () => {
//     return useMutation(async ({ doctype, key, settings }) => {
//         const payload = {
//             doctype,
//             key,
//             settings: JSON.stringify(settings),
//         };
//         const response = await axios.post(
//             "method/frappe.model.user_settings.save",
//             payload
//         );
//         return response.data;
//     });
// };

export const useGetUserSettings = ({ doctype }) => {
    const data = useQuery(
        ["GetUserSettings", doctype],
        async () => {
            const params = {
                doctype,
            };
            const response = await axios.get(
                "method/frappe.model.utils.user_settings.get",
                { params }
            );
            return JSON.parse(response.data.message);
        },
        {
            enabled: !!doctype,
        }
    );
    return data;
};

export const useSaveUserSettings = () => {
    return useMutation(async ({ doctype, user_settings }) => {
        const payload = {
            doctype,
            user_settings: JSON.stringify(user_settings),
        };
        const data = await axios.post(
            "method/frappe.model.utils.user_settings.save",
            payload
        );
        return data;
    });
};

export const useGetCurrentCompany = () => {
    return useQuery([`GetCurrentCompany`], async () => {
        const { data } = await axios.get(
            "method/custom_api.api.get_company_details"
        );
        return data;
    });
};

//
// export const useSaveUserSettings = (onSuccess) => {
//     return useMutation(
//         async ({ doctype, key, settings }) => {
//             const response = await frappe.call({
//                 method: 'frappe.model.user_settings.save',
//                 args: {
//                     doctype: doctype,
//                     key: key,
//                     settings: settings,
//                 },
//             });
//             return response;
//         },
//         {
//             onSuccess: (data) => {
//                 onSuccess?.(data);
//             },
//             onError: (err) => {
//                 console.error(err);
//                 // Handle error appropriately
//             },
//         }
//     );
// };


export const useSiteName = (enabled) => {
    const key = [`sitename`];
    const data = useQuery(
      key,
      async () => {
        const response = await axios.get(`method/custom_api.api.currentSite`);
        return response.data.message;
      },
      {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        cacheTime: Infinity,
      }
    );
  
    return data;
  };